/**
 * useMicGate — le micro de l'élève tenu jusqu'à son geste (`micGate`), branché :
 *  - le choix de la salle d'attente (`liveMode.isAudioMuted`), effacé dès que le micro s'ouvre ou que le
 *    masteur repasse l'élève en Dialogue ;
 *  - l'accès, lu sans rien ouvrir (au web, `deviceAccessReady`) : obtenu dans la page, ou dit accordé (la
 *    règle du souvenir du test, `accessOf`) — la règle que suit aussi le guidage de la question. Le natif n'a pas de rechargement : l'accès y est
 *    celui de l'app, acquis d'emblée ;
 *  - la salle tenue (`holdLiveKitMicrophone`) : elle n'ouvre pas le micro à sa connexion, et l'écran ne
 *    monte `MuteCurrentUser` qu'une fois le micro libre.
 *
 * La demande elle-même (« Activer mon micro », le micro de la barre) part du micro refusé
 * (`useMicRefusal.request`), qui en dit l'échec.
 */
import { useCallback, useEffect, useReducer, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"

import { holdLiveKitMicrophone } from "../../../data/redux/actions/liveKitRoom"
import { setMuteAudio } from "../../../data/redux/actions/videoCall"
import { deviceAccessReady } from "../../precall/deviceAccess"
import { markMicrophoneGranted, microphoneGrantedInPage } from "../../precall/microphoneGrant"
import { IS_WEB } from "../../shared/platform"
import { type EngineTelemetry } from "../../shared/telemetry"
import { initialMicGate, micGateReducer, micGateView, type MicGateView } from "./micGate"

export type UseMicGateOptions = {
    dialogue: boolean
    /** Le mode est une décision de la séance (une réponse, un attribut), plus le défaut d'avant le relevé. */
    dialogueKnown: boolean
    micOn: boolean
    /** La salle de la séance est connectée : la boîte attend la connexion. */
    connected: boolean
    /** Un refus est déjà dit (`micRefusal`) : la boîte du refus passe devant. */
    refused: boolean
    telemetry: EngineTelemetry
}

export type MicGateController = MicGateView & {
    /** « Plus tard ». */
    dismiss: () => void
    /**
     * La demande du geste a ouvert le micro : la porte s'ouvre dans le même rendu que la fin de la demande,
     * sans attendre que l'écran apprenne le micro ouvert (« Active ton micro » revenait un instant entre les
     * deux).
     */
    opened: () => void
}

type RootState = { liveMode?: { isAudioMuted?: boolean } }

export function useMicGate(options: UseMicGateOptions): MicGateController {
    const { dialogue, dialogueKnown, micOn, connected, refused, telemetry } = options
    const dispatch = useDispatch()
    const chosenOff = useSelector((state: RootState) => state.liveMode?.isAudioMuted === true)
    const [state, send] = useReducer(micGateReducer, null, () => initialMicGate(!IS_WEB || microphoneGrantedInPage()))
    const view = micGateView(state, { chosenOff, dialogue, refused, connected })

    useEffect(() => {
        if (state.access !== "reading") return undefined
        let cancelled = false
        void deviceAccessReady("microphone").then((ready) => {
            if (cancelled) return
            telemetry.transition("mic_reading", ready ? "mic_ready" : "mic_ask", "access_read")
            send({ type: "accessRead", ready })
        })
        return () => {
            cancelled = true
        }
    }, [state.access, telemetry])

    // La salle lit le drapeau à sa connexion : il est posé dès l'ouverture, avant le premier relevé
    // qui lui donne son token, et retiré quand le live se démonte.
    useEffect(() => {
        dispatch(holdLiveKitMicrophone(view.held) as never)
    }, [view.held, dispatch])
    useEffect(() => () => void dispatch(holdLiveKitMicrophone(false) as never), [dispatch])

    // Le choix de la salle d'attente ne vaut que jusqu'au micro rouvert : un rechargement ensuite ne le
    // recoupe pas. Lu par les effets sans les relancer quand il change.
    const chosenOffRef = useRef(chosenOff)
    chosenOffRef.current = chosenOff
    const clearChoice = useCallback(() => {
        if (chosenOffRef.current) dispatch(setMuteAudio(false) as never)
    }, [dispatch])

    const stateRef = useRef(state)
    stateRef.current = state
    useEffect(() => {
        if (!micOn) return
        markMicrophoneGranted()
        if (stateRef.current.access !== "ready" || stateRef.current.dismissed) send({ type: "micOpened" })
        clearChoice()
    }, [micOn, clearChoice])

    // Un passage en Dialogue, pas le mode lu à l'ouverture : le défaut (Concentré) puis la réponse du
    // serveur (Dialogue) n'est pas un geste du masteur, et rouvrirait un micro coupé avant d'entrer.
    const wasRef = useRef({ dialogue, known: dialogueKnown })
    useEffect(() => {
        const entered = dialogue && !wasRef.current.dialogue && wasRef.current.known
        wasRef.current = { dialogue, known: dialogueKnown }
        if (!entered) return
        if (stateRef.current.dismissed) send({ type: "dialogueEntered" })
        clearChoice()
    }, [dialogue, dialogueKnown, clearChoice])

    const askedRef = useRef(false)
    useEffect(() => {
        if (view.ask === askedRef.current) return
        askedRef.current = view.ask
        telemetry.transition(view.ask ? "mic_held" : "mic_ask", view.ask ? "mic_ask" : "mic_held", "ask_box")
    }, [view.ask, telemetry])

    const dismiss = useCallback(() => send({ type: "dismissed" }), [])
    const opened = useCallback(() => {
        markMicrophoneGranted()
        send({ type: "micOpened" })
    }, [])

    return { ...view, dismiss, opened }
}
